import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import businessDashboardApi from "../../../api/v3/businessDashboardApi";

const DASHBOARD_CACHE_TTL = 5 * 60 * 1000;
const DASHBOARD_STALE_TIME = DASHBOARD_CACHE_TTL;
const DASHBOARD_CACHE_TIME = DASHBOARD_CACHE_TTL;
const dashboardRequestCache = new Map();
const dashboardInFlightRequests = new Map();
const dashboardTabDataCache = new Map();
const dashboardRequestCacheVersions = new Map();
const dashboardTabDataCacheVersions = new Map();
let dashboardRefreshVersion = 0;

const DASHBOARD_FILTER_PARAM_KEYS = ["start_date", "end_date", "timezone", "aircraft_reg", "route_code", "customer_id", "contract_id", "status"];
const DASHBOARD_TAB_KEYS = ["overview", "aircraft", "routes", "customers", "partners"];

const dashboardRequestKey = (name, params) => JSON.stringify(["businessDashboard", name, params]);
const dashboardTabDataKey = (tabKey, params) => JSON.stringify(["businessDashboardTab", tabKey, params]);

const dashboardErrorMessage = (error) => error?.response?.data?.detail || error?.message || "请求失败";

const normalizeDashboardParamValue = (value) => {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    return value;
};

const dashboardParamsMatch = (params, baseParams) =>
    DASHBOARD_FILTER_PARAM_KEYS.every(
        (key) => normalizeDashboardParamValue(params?.[key]) === normalizeDashboardParamValue(baseParams?.[key]),
    );

const parseDashboardCacheKey = (cacheKey) => {
    try {
        const parsed = JSON.parse(cacheKey);
        if (!Array.isArray(parsed) || parsed.length < 3) {
            return null;
        }
        return parsed;
    } catch (_error) {
        return null;
    }
};

const getCacheVersion = (versionMap, cacheKey) => versionMap.get(cacheKey) || 0;

const bumpCacheVersion = (versionMap, cacheKey) => {
    const nextVersion = getCacheVersion(versionMap, cacheKey) + 1;
    versionMap.set(cacheKey, nextVersion);
    return nextVersion;
};

const clearMatchingDashboardCache = (cache, namespace, baseParams, versionMap) => {
    Array.from(cache.keys()).forEach((cacheKey) => {
        const parsed = parseDashboardCacheKey(cacheKey);
        if (!parsed || parsed[0] !== namespace || !dashboardParamsMatch(parsed[2], baseParams)) {
            return;
        }
        cache.delete(cacheKey);
        if (versionMap) {
            bumpCacheVersion(versionMap, cacheKey);
        }
    });
};

const clearDashboardCachesForParams = (baseParams) => {
    dashboardRefreshVersion += 1;
    clearMatchingDashboardCache(dashboardRequestCache, "businessDashboard", baseParams, dashboardRequestCacheVersions);
    clearMatchingDashboardCache(dashboardInFlightRequests, "businessDashboard", baseParams, dashboardRequestCacheVersions);
    clearMatchingDashboardCache(dashboardTabDataCache, "businessDashboardTab", baseParams, dashboardTabDataCacheVersions);

    DASHBOARD_TAB_KEYS.forEach((tabKey) => {
        bumpCacheVersion(dashboardTabDataCacheVersions, dashboardTabDataKey(tabKey, baseParams));
    });

    return dashboardRefreshVersion;
};

const createPartialRequestError = (partialErrors, fallbackMessage) => {
    const error = new Error(
        partialErrors?.length
            ? partialErrors.map((item) => `${item.label}：${item.message}`).join("；")
            : fallbackMessage,
    );
    error.partialErrors = partialErrors || [];
    return error;
};

const settleDashboardQueries = async (queries) => {
    const results = await Promise.all(
        queries.map(async (item) => {
            try {
                return {
                    key: item.key,
                    data: await item.query(),
                    ok: true,
                };
            } catch (error) {
                return {
                    key: item.key,
                    label: item.label,
                    message: dashboardErrorMessage(error),
                    error,
                    ok: false,
                };
            }
        }),
    );

    return results.reduce(
        (acc, item) => {
            if (item.ok) {
                acc.data[item.key] = item.data;
            } else {
                acc.partialErrors.push({
                    key: item.key,
                    label: item.label,
                    message: item.message,
                    error: item.error,
                });
            }
            return acc;
        },
        { data: {}, partialErrors: [] },
    );
};

const mergeDataQuality = (responses) => {
    const warnings = [];
    let level = "COMPLETE";

    responses.forEach((response) => {
        const dataQuality = response?.data_quality;
        if (!dataQuality) return;

        if (dataQuality.level === "MISSING") {
            level = "MISSING";
        } else if (dataQuality.level === "PARTIAL" && level !== "MISSING") {
            level = "PARTIAL";
        }

        (dataQuality.warnings || []).forEach((warning) => {
            warnings.push({
                code: warning.code,
                message: warning.message,
                severity: warning.severity || "INFO",
            });
        });
    });

    return {
        level,
        warnings,
    };
};

const emptyTabData = {
    overview: {},
    aircraft: {},
    routes: {},
    customers: {},
    partners: {},
};

export function useBusinessDashboardData({ filters, hasAmountPermission }) {
    const queryClient = useQueryClient();
    const [sharedData, setSharedData] = useState({
        overview: null,
        dataQuality: null,
        bhSummary: null,
        bhTimeSeries: null,
        risks: null,
        events: null,
        cargoSummary: null,
        capacityOutlook: null,
        salesSummary: null,
        routesPerformance: null,
    });
    const [tabData, setTabData] = useState(emptyTabData);
    const [loading, setLoading] = useState({});
    const [errors, setErrors] = useState({});
    const [loadedTabs, setLoadedTabs] = useState({});
    const [forceRefreshTabs, setForceRefreshTabs] = useState({});

    const requestParams = useMemo(
        () => ({
            start_date: filters.start_date,
            end_date: filters.end_date,
            timezone: filters.timezone || "Asia/Shanghai",
            aircraft_reg: filters.aircraft_reg,
            route_code: filters.route_code,
            customer_id: filters.customer_id,
            contract_id: filters.contract_id,
            status: filters.status,
        }),
        [filters],
    );

    const requestSignature = useMemo(() => JSON.stringify(requestParams), [requestParams]);

    useEffect(() => {
        setLoadedTabs({});
        setTabData(emptyTabData);
        setForceRefreshTabs({});
    }, [requestSignature]);

    const setLoadingKey = useCallback((key, value) => {
        setLoading((prev) => ({ ...prev, [key]: value }));
    }, []);

    const setErrorKey = useCallback((key, value) => {
        setErrors((prev) => ({ ...prev, [key]: value }));
    }, []);

    const fetchDashboardQuery = useCallback(
        async (name, params, fetcher, { force = false } = {}) => {
            const queryKey = ["businessDashboard", name, params];
            const cacheKey = dashboardRequestKey(name, params);
            const cached = dashboardRequestCache.get(cacheKey);
            if (!force && cached && Date.now() - cached.updatedAt < DASHBOARD_STALE_TIME) {
                return cached.data;
            }
            const inFlight = dashboardInFlightRequests.get(cacheKey);
            if (!force && inFlight) {
                return inFlight;
            }
            if (force) {
                dashboardRequestCache.delete(cacheKey);
                dashboardInFlightRequests.delete(cacheKey);
                bumpCacheVersion(dashboardRequestCacheVersions, cacheKey);
                await queryClient.invalidateQueries(queryKey);
            }

            const requestCacheVersion = getCacheVersion(dashboardRequestCacheVersions, cacheKey);
            const requestPromise = (async () => {
                const networkParams = force ? { ...params, force_refresh: true } : params;
                const data = await queryClient.fetchQuery(queryKey, () => fetcher(networkParams), {
                    staleTime: DASHBOARD_STALE_TIME,
                    cacheTime: DASHBOARD_CACHE_TIME,
                    retry: false,
                });
                if (getCacheVersion(dashboardRequestCacheVersions, cacheKey) === requestCacheVersion) {
                    dashboardRequestCache.set(cacheKey, { data, updatedAt: Date.now() });
                }
                return data;
            })();

            dashboardInFlightRequests.set(cacheKey, requestPromise);
            try {
                return await requestPromise;
            } finally {
                if (dashboardInFlightRequests.get(cacheKey) === requestPromise) {
                    dashboardInFlightRequests.delete(cacheKey);
                }
            }
        },
        [queryClient],
    );

    const loadSharedDeferred = useCallback(
        async (options = {}) => {
            const force = options?.force === true;
            const refreshVersion = dashboardRefreshVersion;
            setLoadingKey("sharedDeferred", true);
            setErrorKey("sharedDeferred", null);
            try {
                const eventsParams = { ...requestParams, page: 1, page_size: 20 };
                const { data: deferredData, partialErrors } = await settleDashboardQueries([
                    {
                        key: "events",
                        label: "重大异常事件",
                        query: () => fetchDashboardQuery("eventsTimeline", eventsParams, businessDashboardApi.getEventsTimeline, { force }),
                    },
                    {
                        key: "cargoSummary",
                        label: "货载与 ULD",
                        query: () => fetchDashboardQuery("cargoSummary", requestParams, businessDashboardApi.getCargoSummary, { force }),
                    },
                    {
                        key: "capacityOutlook",
                        label: "未来运力锁定",
                        query: () => fetchDashboardQuery("capacityOutlook", requestParams, businessDashboardApi.getCapacityOutlook, { force }),
                    },
                    {
                        key: "routesPerformance",
                        label: "航线网络态势",
                        query: () => fetchDashboardQuery("routesPerformance", requestParams, businessDashboardApi.getRoutesPerformance, { force }),
                    },
                    {
                        key: "salesSummary",
                        label: "销售经营摘要",
                        query: () =>
                            hasAmountPermission
                                ? fetchDashboardQuery("salesSummary", requestParams, businessDashboardApi.getSalesSummary, { force })
                                : Promise.resolve(null),
                    },
                ]);

                if (refreshVersion !== dashboardRefreshVersion) {
                    return deferredData;
                }

                setSharedData((prev) => ({ ...prev, ...deferredData }));
                if (partialErrors.length) {
                    setErrorKey("sharedDeferred", createPartialRequestError(partialErrors, "部分延迟数据加载失败"));
                }
                return deferredData;
            } catch (error) {
                if (refreshVersion !== dashboardRefreshVersion) {
                    return {};
                }
                setErrorKey("sharedDeferred", error);
                return {};
            } finally {
                if (refreshVersion === dashboardRefreshVersion) {
                    setLoadingKey("sharedDeferred", false);
                }
            }
        },
        [fetchDashboardQuery, hasAmountPermission, requestParams, setErrorKey, setLoadingKey],
    );

    const loadShared = useCallback(async (options = {}) => {
        const force = options?.force === true;
        const waitForDeferred = options?.waitForDeferred === true;
        const refreshVersion = dashboardRefreshVersion;
        setLoadingKey("shared", true);
        setErrorKey("shared", null);
        try {
            const bhTimeSeriesParams = { ...requestParams, granularity: "day" };
            const [overview, dataQuality, bhSummary, bhTimeSeries, risks] = await Promise.all([
                fetchDashboardQuery("overview", requestParams, businessDashboardApi.getOverview, { force }),
                fetchDashboardQuery("dataQuality", requestParams, businessDashboardApi.getDataQuality, { force }),
                fetchDashboardQuery("bhSummary", requestParams, businessDashboardApi.getBhSummary, { force }),
                fetchDashboardQuery("bhTimeSeries", bhTimeSeriesParams, businessDashboardApi.getBhTimeSeries, { force }),
                fetchDashboardQuery("risks", requestParams, businessDashboardApi.getRisks, { force }),
            ]);

            const criticalData = {
                overview,
                dataQuality,
                bhSummary,
                bhTimeSeries,
                risks,
            };
            if (refreshVersion !== dashboardRefreshVersion) {
                return criticalData;
            }

            setSharedData({
                ...criticalData,
                events: null,
                cargoSummary: null,
                capacityOutlook: null,
                salesSummary: null,
                routesPerformance: null,
            });

            const deferredPromise = loadSharedDeferred({ force });
            if (waitForDeferred) {
                const deferredData = await deferredPromise;
                return { ...criticalData, ...deferredData };
            }

            deferredPromise.catch(() => undefined);
            return criticalData;
        } catch (error) {
            if (refreshVersion === dashboardRefreshVersion) {
                setErrorKey("shared", error);
            }
            throw error;
        } finally {
            if (refreshVersion === dashboardRefreshVersion) {
                setLoadingKey("shared", false);
            }
        }
    }, [fetchDashboardQuery, loadSharedDeferred, requestParams, setErrorKey, setLoadingKey]);

    const loadTab = useCallback(
        async (tabKey, { force = false } = {}) => {
            const shouldForce = force || forceRefreshTabs[tabKey] === requestSignature;
            if (!shouldForce && loadedTabs[tabKey]) {
                return tabData[tabKey];
            }

            const tabCacheKey = dashboardTabDataKey(tabKey, requestParams);
            if (shouldForce) {
                dashboardTabDataCache.delete(tabCacheKey);
                bumpCacheVersion(dashboardTabDataCacheVersions, tabCacheKey);
            }
            const tabCacheVersion = getCacheVersion(dashboardTabDataCacheVersions, tabCacheKey);
            const cachedTabData = dashboardTabDataCache.get(tabCacheKey);
            if (!shouldForce && cachedTabData && Date.now() - cachedTabData.updatedAt < DASHBOARD_CACHE_TTL) {
                setErrorKey(tabKey, null);
                setTabData((prev) => ({ ...prev, [tabKey]: cachedTabData.data }));
                setLoadedTabs((prev) => ({ ...prev, [tabKey]: true }));
                return cachedTabData.data;
            }

            setLoadingKey(tabKey, true);
            setErrorKey(tabKey, null);

            try {
                let nextData = {};
                if (tabKey === "aircraft") {
                    const [aircraft, currentStatus, bhByAircraft, availability, operatorGroups] = await Promise.all([
                        fetchDashboardQuery("aircraft", requestParams, businessDashboardApi.getAircraft, { force: shouldForce }),
                        fetchDashboardQuery("aircraftCurrentStatus", requestParams, businessDashboardApi.getAircraftCurrentStatus, { force: shouldForce }),
                        fetchDashboardQuery("bhByAircraft", requestParams, businessDashboardApi.getBhByAircraft, { force: shouldForce }),
                        fetchDashboardQuery("aircraftAvailability", requestParams, businessDashboardApi.getAircraftAvailability, { force: shouldForce }),
                        fetchDashboardQuery("operatorGroups", requestParams, businessDashboardApi.getOperatorGroups, { force: shouldForce }),
                    ]);
                    nextData = { aircraft, currentStatus, bhByAircraft, availability, operatorGroups };
                } else if (tabKey === "routes") {
                    const [routesPerformance, routesOtp, routesLoop, bhByRoute, cargoByRoute] = await Promise.all([
                        fetchDashboardQuery("routesPerformance", requestParams, businessDashboardApi.getRoutesPerformance, { force: shouldForce }),
                        fetchDashboardQuery("routesOtp", requestParams, businessDashboardApi.getRoutesOtp, { force: shouldForce }),
                        fetchDashboardQuery("routesLoop", requestParams, businessDashboardApi.getRoutesLoop, { force: shouldForce }),
                        fetchDashboardQuery("bhByRoute", requestParams, businessDashboardApi.getBhByRoute, { force: shouldForce }),
                        fetchDashboardQuery("cargoByRoute", requestParams, businessDashboardApi.getCargoByRoute, { force: shouldForce }),
                    ]);
                    nextData = { routesPerformance, routesOtp, routesLoop, bhByRoute, cargoByRoute };
                } else if (tabKey === "customers") {
                    const customersParams = { ...requestParams, page: 1, page_size: 50 };
                    const [customers, commercialFlights] = await Promise.all([
                        fetchDashboardQuery("customersPerformance", customersParams, businessDashboardApi.getCustomersPerformance, { force: shouldForce }),
                        fetchDashboardQuery("commercialFlights", customersParams, businessDashboardApi.getCommercialFlights, { force: shouldForce }),
                    ]);
                    nextData = { customers, commercialFlights };
                } else if (tabKey === "partners") {
                    const partnersParams = { ...requestParams, page: 1, page_size: 50 };
                    const { data, partialErrors } = await settleDashboardQueries([
                        {
                            key: "partners",
                            label: "托管航司与供应商表现",
                            query: () => fetchDashboardQuery("partnersPerformance", partnersParams, businessDashboardApi.getPartnersPerformance, { force: shouldForce }),
                        },
                        {
                            key: "events",
                            label: "关联异常事件",
                            query: () => fetchDashboardQuery("partnersEventsTimeline", partnersParams, businessDashboardApi.getEventsTimeline, { force: shouldForce }),
                        },
                        {
                            key: "aogEvents",
                            label: "AOG 事件",
                            query: () => fetchDashboardQuery("aogEvents", partnersParams, businessDashboardApi.getAogEvents, { force: shouldForce }),
                        },
                    ]);
                    if (!Object.keys(data).length) {
                        throw createPartialRequestError(partialErrors, "托管航司与供应商数据加载失败");
                    }
                    nextData = { ...data, partialErrors };
                }

                if (getCacheVersion(dashboardTabDataCacheVersions, tabCacheKey) === tabCacheVersion) {
                    setTabData((prev) => ({ ...prev, [tabKey]: nextData }));
                    dashboardTabDataCache.set(tabCacheKey, { data: nextData, updatedAt: Date.now() });
                    setLoadedTabs((prev) => ({ ...prev, [tabKey]: true }));
                    setForceRefreshTabs((prev) => {
                        if (prev[tabKey] !== requestSignature) {
                            return prev;
                        }
                        const next = { ...prev };
                        delete next[tabKey];
                        return next;
                    });
                }
                return nextData;
            } catch (error) {
                if (getCacheVersion(dashboardTabDataCacheVersions, tabCacheKey) === tabCacheVersion) {
                    setErrorKey(tabKey, error);
                }
                throw error;
            } finally {
                if (getCacheVersion(dashboardTabDataCacheVersions, tabCacheKey) === tabCacheVersion) {
                    setLoadingKey(tabKey, false);
                }
            }
        },
        [fetchDashboardQuery, forceRefreshTabs, loadedTabs, requestParams, requestSignature, setErrorKey, setLoadingKey, tabData],
    );

    const reloadAll = useCallback(
        async (activeTab) => {
            const refreshVersion = clearDashboardCachesForParams(requestParams);
            await queryClient.invalidateQueries(["businessDashboard"]);
            setForceRefreshTabs(
                DASHBOARD_TAB_KEYS.reduce((acc, tabKey) => {
                    if (tabKey !== "overview" && tabKey !== activeTab) {
                        acc[tabKey] = requestSignature;
                    }
                    return acc;
                }, {}),
            );
            setLoadedTabs({});
            setTabData(emptyTabData);
            const shared = await loadShared({ force: true });
            if (refreshVersion !== dashboardRefreshVersion) {
                return shared;
            }
            if (activeTab && activeTab !== "overview") {
                await loadTab(activeTab, { force: true });
            }
            return shared;
        },
        [loadShared, loadTab, queryClient, requestParams, requestSignature],
    );

    const dataQuality = useMemo(
        () =>
            mergeDataQuality([
                sharedData.overview,
                sharedData.dataQuality,
                sharedData.bhSummary,
                sharedData.bhTimeSeries,
                sharedData.risks,
                sharedData.events,
                sharedData.cargoSummary,
                sharedData.capacityOutlook,
                sharedData.salesSummary,
                sharedData.routesPerformance,
                ...Object.values(tabData).flatMap((item) => Object.values(item || {})),
            ]),
        [sharedData, tabData],
    );

    return {
        sharedData,
        tabData,
        loading,
        errors,
        dataQuality,
        loadShared,
        loadTab,
        reloadAll,
        requestParams,
    };
}

export default useBusinessDashboardData;
